import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { useLocation } from 'react-router';
import * as Sentry from '@sentry/react';
import { isExtensionError } from '../utils/extension-error-filter';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// Router-aware ErrorBoundary that resets on navigation
function RouterWithErrorBoundary({ children }: Props) {
  const location = useLocation();

  return (
    <ErrorBoundary key={location.pathname} location={location.pathname}>
      {children}
    </ErrorBoundary>
  );
}

export class ErrorBoundary extends Component<
  Props & { location?: string },
  State
> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidUpdate(prevProps: Props & { location?: string }) {
    // Reset error state when location changes
    if (this.props.location !== prevProps.location && this.state.hasError) {
      this.setState({
        hasError: false,
        error: null,
      });
    }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);

    // Check if this error comes from a browser extension
    const extensionCheck = isExtensionError(error);
    if (extensionCheck.isExtensionError) {
      console.log(
        '🚫 Blocked extension error from Sentry:',
        extensionCheck.reason,
        error,
      );
      return; // Don't send to Sentry
    }

    // Capture the exception in Sentry for tracking
    Sentry.captureException(error);
  }

  // Method to manually reset error state
  public resetError = () => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className='flex flex-col items-center justify-center min-h-[80vh] text-center px-4'>
          <AlertTriangle className='h-12 w-12 text-yellow-500 mb-4' />
          <h1 className='text-2xl font-bold mb-4 text-foreground'>
            Oops! Something went wrong
          </h1>
          <p className='text-muted-foreground mb-6 max-w-md'>
            We apologize for the inconvenience. Please try refreshing the page
            or contact support if the problem persists.
          </p>
          <div className='flex gap-4'>
            <Button
              onClick={() => window.location.reload()}
              className='rounded-full px-6'
            >
              Refresh Page
            </Button>
            <Button
              variant='outline'
              className='rounded-full px-6'
              onClick={() => (window.location.href = '/')}
            >
              Go to main page
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Export the router version as default
export { RouterWithErrorBoundary };
